import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Briefcase, ShoppingCart, Calendar, LogOut, MapPin, DollarSign,
  Clock, CheckCircle, FileText, HardHat
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400' },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400' },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-AU', { 
    day: '2-digit', month: 'short', year: 'numeric', 
    hour: '2-digit', minute: '2-digit' 
  });
};

const TradePortalPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pos, setPOs] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('trade_token');
    if (token) {
      fetchTradeData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchTradeData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, jobsRes, posRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/api/trade-portal/me`, { headers }),
        axios.get(`${API}/api/trade-portal/jobs`, { headers }),
        axios.get(`${API}/api/trade-portal/purchase-orders`, { headers }),
        axios.get(`${API}/api/trade-portal/schedule`, { headers }),
      ]);
      setUser(meRes.data.user);
      setContractor(meRes.data.contractor);
      setJobs(jobsRes.data);
      setPOs(posRes.data);
      setSchedule(scheduleRes.data);
      setIsLoggedIn(true);
    } catch (error) {
      localStorage.removeItem('trade_token');
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/trade-portal/login`, { email, password });
      localStorage.setItem('trade_token', response.data.token);
      toast.success('Welcome back!');
      fetchTradeData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/trade-portal/register`, { email, password });
      localStorage.setItem('trade_token', response.data.token);
      toast.success('Account created successfully!');
      fetchTradeData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Make sure you are registered as a contractor.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('trade_token');
    setIsLoggedIn(false);
    setUser(null);
    setContractor(null);
    toast.success('Logged out');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png" 
              alt="ConstructOne" 
              className="h-20 mx-auto mb-4"
            />
            <CardTitle className="text-2xl">{isRegistering ? 'Create Trade Account' : 'Trade Portal'}</CardTitle>
            <p className="text-muted-foreground">Access your jobs, POs, and schedule</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-background" placeholder="Your contractor email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-background" />
              </div>
              <Button type="submit" className="w-full">{isRegistering ? 'Create Account' : 'Sign In'}</Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => setIsRegistering(!isRegistering)}>
                {isRegistering ? 'Already have an account? Sign In' : 'First time? Create Account'}
              </Button>
            </div>
            {isRegistering && (
              <p className="text-xs text-muted-foreground text-center mt-4">
                Note: You must be registered as a contractor by ConstructOne to create an account.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/hptkd9bx_ChatGPT%20Image%20Dec%2027%2C%202025%2C%2001_26_00%20AM.png" 
              alt="ConstructOne" 
              className="h-10"
            />
            <div>
              <h1 className="font-semibold">Trade Portal</h1>
              <p className="text-sm text-muted-foreground">
                {contractor?.name} • {contractor?.trade_type}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout}><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Briefcase className="h-10 w-10 text-primary opacity-50" />
              <div>
                <p className="text-2xl font-bold">{jobs.length}</p>
                <p className="text-sm text-muted-foreground">Assigned Jobs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <ShoppingCart className="h-10 w-10 text-green-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{pos.length}</p>
                <p className="text-sm text-muted-foreground">Purchase Orders</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Calendar className="h-10 w-10 text-blue-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{schedule.filter(s => new Date(s.start_datetime) >= new Date()).length}</p>
                <p className="text-sm text-muted-foreground">Upcoming Tasks</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-secondary/50 mb-6">
            <TabsTrigger value="jobs">My Jobs</TabsTrigger>
            <TabsTrigger value="pos">Purchase Orders</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No jobs assigned yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <Card key={job.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{job.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.address}</span>
                          </div>
                          <p className="text-sm mt-2">Client: {job.client_name}</p>
                          {job.client_phone && <p className="text-sm text-muted-foreground">{job.client_phone}</p>}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[job.status]?.color}`}>
                          {statusConfig[job.status]?.label}
                        </span>
                      </div>
                      {job.description && <p className="text-sm text-muted-foreground mt-3 border-t pt-3">{job.description}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchase Orders Tab */}
          <TabsContent value="pos">
            {pos.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No purchase orders yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pos.map(po => (
                  <Card key={po.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{po.po_number}</h3>
                          <p className="text-sm text-muted-foreground">{po.job?.title} - {po.job?.address}</p>
                          <p className="text-lg font-bold mt-2">${po.total?.toFixed(2)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[po.status]?.color}`}>
                          {statusConfig[po.status]?.label}
                        </span>
                      </div>
                      {po.items && po.items.length > 0 && (
                        <div className="mt-4 border-t pt-3">
                          <p className="text-sm font-medium mb-2">Items:</p>
                          <div className="space-y-1">
                            {po.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{item.description}</span>
                                <span>${item.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {po.notes && <p className="text-xs text-muted-foreground mt-3 italic">{po.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {schedule.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled tasks</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {schedule.map(item => {
                  const isUpcoming = new Date(item.start_datetime) >= new Date();
                  return (
                    <Card key={item.id} className={`bg-card border-border ${!isUpcoming ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{item.title}</h3>
                            <p className="text-sm text-muted-foreground">{item.job?.title} - {item.job?.address}</p>
                            <div className="flex items-center gap-2 mt-2 text-sm">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDateTime(item.start_datetime)}</span>
                              <span className="text-muted-foreground">to</span>
                              <span>{formatDateTime(item.end_datetime)}</span>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs ${
                            isUpcoming ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {isUpcoming ? 'Upcoming' : 'Past'}
                          </span>
                        </div>
                        {item.description && <p className="text-sm text-muted-foreground mt-3">{item.description}</p>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default TradePortalPage;
